import { useState } from "react";
import { Menu, Search, Bell, ChevronDown, Building2 } from "lucide-react";
import { useCompany } from "@/contexts/CompanyContext";
import { useViewMode } from "@/contexts/ViewModeContext";
import ViewModeSelector from "@/components/ViewModeSelector";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";

export default function Topbar({ onToggleSidebar, title, subtitle, showHamburger = false }) {
  const { companies, selected, select, selectedCompany } = useCompany();
  const { effective } = useViewMode();
  const [q, setQ] = useState("");

  const label =
    selected === "all" ? "Grupo ALCON — Consolidado" : selectedCompany?.nome_fantasia || "Selecione";

  const isMobile = effective === "mobile";
  const isTablet = effective === "tablet";

  return (
    <header
      data-testid="topbar"
      className="sticky top-0 z-20 flex items-center gap-3 border-b border-border bg-white/85 backdrop-blur-md px-4 md:px-8 h-16 rh-topbar"
    >
      {showHamburger && (
        <button
          onClick={onToggleSidebar}
          className="grid h-10 w-10 place-items-center rounded-md border border-border text-muted-foreground hover:text-foreground"
          data-testid="toggle-sidebar-btn"
        >
          <Menu className="h-5 w-5" />
        </button>
      )}

      {!isMobile && (
        <div className="flex flex-col leading-tight mr-4 min-w-0">
          <h1 className="font-display text-[18px] font-semibold text-foreground truncate">{title}</h1>
          {subtitle && (
            <span className="text-[11.5px] text-muted-foreground uppercase tracking-[0.18em] truncate">
              {subtitle}
            </span>
          )}
        </div>
      )}

      {isMobile && (
        <div className="flex flex-col leading-tight min-w-0 flex-1">
          <h1 className="font-display text-[15px] font-semibold text-foreground truncate">{title}</h1>
        </div>
      )}

      {/* Search — only desktop */}
      {effective === "desktop" && (
        <div className="flex-1 max-w-md flex items-center">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar colaboradores, setores, cargos..."
              className="pl-9 bg-secondary/60 border-transparent focus-visible:ring-primary"
              data-testid="global-search-input"
            />
          </div>
        </div>
      )}

      <div className="ml-auto flex items-center gap-2">
        <ViewModeSelector compact={isMobile} />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="company-selector-trigger"
              className="flex items-center gap-2 rounded-md border border-border bg-white px-2.5 py-2 text-[13px] font-medium text-foreground hover:bg-secondary transition-colors duration-200"
            >
              <Building2 className="h-4 w-4 text-[#C5A059]" />
              {!isMobile && (
                <span className={`${isTablet ? "max-w-[140px]" : "max-w-[220px]"} truncate hidden sm:inline`}>
                  {label}
                </span>
              )}
              <ChevronDown className="h-3.5 w-3.5 opacity-60" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[260px]">
            <DropdownMenuLabel>Empresa ativa</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="company-select-all"
              onSelect={() => select("all")}
              className={selected === "all" ? "bg-secondary" : ""}
            >
              <div>
                <div className="font-medium">Grupo ALCON</div>
                <div className="text-xs text-muted-foreground">Consolidado (todas as empresas)</div>
              </div>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {companies.map((c) => (
              <DropdownMenuItem
                key={c.id}
                data-testid={`company-select-${c.id}`}
                onSelect={() => select(c.id)}
                className={selected === c.id ? "bg-secondary" : ""}
              >
                <div>
                  <div className="font-medium">{c.nome_fantasia}</div>
                  <div className="text-xs text-muted-foreground">{c.cnpj}</div>
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <button
          className="grid h-9 w-9 place-items-center rounded-md border border-border text-muted-foreground hover:text-foreground transition-colors duration-200"
          data-testid="notifications-btn"
          title="Notificações"
        >
          <Bell className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
}
